/**
 * useLayoutUserId — whose layout this is.
 *
 * The id has to be available BEFORE the first paint, because a surface reads
 * its stored arrangement in a `useState` initialiser: an id that only arrives
 * after the profile request would mean every page opened on the defaults and
 * then jumped. `user` is in the redux-persist whitelist, so on any returning
 * session `state.user.userInfo` is already rehydrated when the tree mounts.
 *
 * Two deliberate softnesses:
 *
 *   NO PROVIDER, NO THROW. `useSelector` throws outside a redux `Provider`,
 *   and surface components are rendered in unit tests without one. Reading
 *   `ReactReduxContext` returns null there instead, and the hook degrades to
 *   the persisted blob and then to `anon` — a test never crashes on this.
 *
 *   FIRST-EVER LOGIN. Nothing is persisted yet, so the id starts `anon` and
 *   arrives with the profile. The hook subscribes so the surface can re-key
 *   when it does (see `usePanelSurface`, which reloads on a surface change).
 */

import { useContext, useEffect, useState } from 'react';
import { ReactReduxContext } from 'react-redux';
import { ANONYMOUS_USER } from './layoutStore';

/** Where redux-persist keeps the whitelisted slices. */
const PERSIST_KEY = 'persist:root';

const idFrom = (userInfo: any): string | null => {
  if (!userInfo || typeof userInfo !== 'object') return null;
  const candidate =
    userInfo.UserUID
    ?? userInfo.uuid_encrypted
    ?? (userInfo.UserID !== undefined && userInfo.UserID !== null
      ? String(userInfo.UserID)
      : null);
  const value = typeof candidate === 'string' ? candidate.trim() : '';
  return value || null;
};

/** The id in the live store, if there is one. */
export function userIdFromState(state: any): string | null {
  return idFrom(state?.user?.userInfo);
}

/**
 * The id redux-persist wrote last time.
 *
 * Used only when there is no store in the tree. The slices are stored as
 * JSON-inside-JSON, which is redux-persist's own format, not ours.
 */
export function persistedUserId(
  storage: Pick<Storage, 'getItem'> | null = typeof window === 'undefined'
    ? null
    : window.localStorage
): string | null {
  try {
    const raw = storage?.getItem(PERSIST_KEY);
    if (!raw) return null;
    const root = JSON.parse(raw);
    const slice = root?.user;
    const user = typeof slice === 'string' ? JSON.parse(slice) : slice;
    return idFrom(user?.userInfo);
  } catch {
    return null;
  }
}

export function resolveLayoutUserId(state?: any): string {
  return userIdFromState(state) ?? persistedUserId() ?? ANONYMOUS_USER;
}

export function useLayoutUserId(): string {
  const context = useContext(ReactReduxContext);
  const store = context?.store;
  const [userId, setUserId] = useState<string>(() =>
    resolveLayoutUserId(store?.getState())
  );

  useEffect(() => {
    if (!store) return undefined;
    const read = () => {
      const next = resolveLayoutUserId(store.getState());
      setUserId((current) => (current === next ? current : next));
    };
    read();
    return store.subscribe(read);
  }, [store]);

  return userId;
}

export default useLayoutUserId;
